import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fetchThreads, fetchForumTags, createThread } from "@/lib/api";
import AttachImages from "@/components/community/AttachImages";
import { PollComposer } from "@/components/community/ThreadMedia";
import { MessageSquare, ArrowBigUp, CheckCircle2, Search, Plus, Loader2, Tag as TagIcon, X, Image as ImageIcon, BarChart3 } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const emptyPoll = { question: "", options: ["", ""], multi: false };

export default function ForumList({ kind, basePath, groupId = null }) {
  const { pick } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isQna = kind === "qna";
  const [threads, setThreads] = useState([]);
  const [tags, setTags] = useState([]);
  const [sort, setSort] = useState("recent");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tags: "" });
  const [images, setImages] = useState([]);
  const [poll, setPoll] = useState(null);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchThreads({ kind, sort, ...(tag ? { tag } : {}), ...(q ? { q } : {}), ...(groupId ? { group_id: groupId } : {}) });
      setThreads(data);
    } finally { setLoading(false); }
  }, [kind, sort, tag, q, groupId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!groupId) fetchForumTags(kind).then(setTags).catch(() => {}); }, [kind, groupId]);

  const submit = async (e) => {
    e.preventDefault();
    const validPoll = poll && poll.options.filter((o) => o.trim()).length >= 2 ? { question: poll.question, options: poll.options.filter((o) => o.trim()), multi: poll.multi } : null;
    if (!form.title.trim() || (!form.body.trim() && !images.length && !validPoll)) {
      toast(pick("शीर्षक आवश्यक", "Title required"), { description: pick("शीर्षक और कुछ विवरण, चित्र या मतदान जोड़ें।", "Add a title and some details, an image, or a poll.") });
      return;
    }
    setPosting(true);
    try {
      const t = await createThread({
        kind, title: form.title, body: form.body,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        images, poll: validPoll, ...(groupId ? { group_id: groupId } : {}),
      });
      toast(isQna ? pick("प्रश्न प्रकाशित", "Question posted") : pick("चर्चा आरंभ", "Discussion started"), { description: "+5 sadhana points" });
      if (groupId) { setComposing(false); setForm({ title: "", body: "", tags: "" }); setImages([]); setPoll(null); load(); }
      else navigate(`${basePath}/${t.id}`);
    } catch (err) {
      toast(pick("प्रकाशित नहीं हो सका", "Could not post"), { description: err?.response?.data?.detail || pick("पुनः प्रयास करें।", "Please try again.") });
    } finally { setPosting(false); }
  };

  const sorts = isQna
    ? [["recent", "Recent"], ["top", "Top"], ["unanswered", "Unanswered"]]
    : [["recent", "Recent"], ["top", "Top"]];

  return (
    <section className={groupId ? "" : "mx-auto max-w-4xl px-5 py-12 md:px-8"}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="forum-search"
            placeholder={pick("खोजें…", "Search…")}
            className="w-full border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink focus:border-saffron focus:outline-none" />
        </div>
        <div className="flex border border-line">
          {sorts.map(([k, l]) => (
            <button key={k} onClick={() => setSort(k)} data-testid={`forum-sort-${k}`}
              className={`px-3 py-2 text-xs transition-colors ${sort === k ? "bg-saffron text-white" : "text-ink-2 hover:text-ink"}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => (user ? setComposing((v) => !v) : navigate("/login"))} data-testid="forum-new-button"
          className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
          <Plus className="h-4 w-4" /> {isQna ? pick("प्रश्न पूछें", "Ask") : pick("नई चर्चा", "Start")}
        </button>
      </div>

      {/* Tag chips */}
      {tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {tag && <button onClick={() => setTag("")} className="inline-flex items-center gap-1 border border-saffron px-2.5 py-1 text-xs text-saffron"><X className="h-3 w-3" /> {tag}</button>}
          {tags.filter((t) => t.tag !== tag).slice(0, 12).map((t) => (
            <button key={t.tag} onClick={() => setTag(t.tag)} data-testid={`forum-tag-${t.tag}`}
              className="inline-flex items-center gap-1 border border-line px-2.5 py-1 text-xs text-ink-2 transition-colors hover:border-[var(--saffron)] hover:text-saffron">
              <TagIcon className="h-3 w-3 text-gold" /> {t.tag} <span className="text-ink-3">{t.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      {composing && user && (
        <form onSubmit={submit} data-testid="forum-composer" className="mt-5 border border-line bg-surface p-5">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="forum-title-input"
            placeholder={isQna ? pick("आपका प्रश्न क्या है?", "What is your question?") : pick("चर्चा का शीर्षक", "Discussion title")}
            className="w-full border border-line bg-parchment px-3 py-2 text-ink focus:border-saffron focus:outline-none" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="forum-body-input" rows={5}
            placeholder={pick("विस्तार से लिखें…", "Share the details…")}
            className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
          {!groupId && (
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="forum-tags-input"
              placeholder={pick("टैग (कॉमा से अलग): शिव, व्रत", "Tags (comma separated): shiva, vrata")}
              className="mt-3 w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <AttachImages images={images} setImages={setImages} />
            <button type="button" onClick={() => setPoll(poll ? null : { ...emptyPoll })} data-testid="forum-poll-toggle"
              className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors ${poll ? "border-saffron text-saffron" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
              <BarChart3 className="h-3.5 w-3.5" /> {pick("मतदान", "Poll")}
            </button>
          </div>
          {poll && <PollComposer poll={poll} setPoll={setPoll} />}
          <div className="mt-3 flex gap-2">
            <button type="submit" disabled={posting} data-testid="forum-submit" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {pick("प्रकाशित करें", "Post")}
            </button>
            <button type="button" onClick={() => setComposing(false)} className="border border-line px-4 py-2 text-sm text-ink-2">{pick("रद्द", "Cancel")}</button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="mt-6 divide-y divide-line border-y border-line">
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> {pick("लोड हो रहा…", "Loading…")}</div>
        ) : threads.length === 0 ? (
          <div className="py-14 text-center text-ink-3" data-testid="forum-empty">
            {pick("अभी तक कुछ नहीं। पहले बनें!", "Nothing here yet. Be the first!")}
          </div>
        ) : threads.map((t) => (
          <Link key={t.id} to={`${basePath}/${t.id}`} data-testid={`thread-card-${t.id}`} className="group flex gap-4 py-5">
            <div className="flex w-12 shrink-0 flex-col items-center pt-1 text-ink-3">
              <ArrowBigUp className="h-5 w-5" />
              <span className="text-sm font-medium text-ink-2">{t.upvotes}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <h3 className="font-serif-latin text-lg text-ink transition-colors group-hover:text-saffron">{t.title}</h3>
                {isQna && t.best_answer_id && <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-green-600" title="Answered" />}
              </div>
              {t.body && <p className="mt-1 line-clamp-2 text-sm text-ink-2">{t.body}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-3">
                <span>{t.author_name}</span>
                <span>{timeAgo(t.created_at)}</span>
                <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {t.reply_count}</span>
                {t.poll && <span className="inline-flex items-center gap-1 text-gold"><BarChart3 className="h-3 w-3" /> {pick("मतदान", "Poll")}</span>}
                {t.images?.length > 0 && <span className="inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" /> {t.images.length}</span>}
                {(t.tags || []).map((tg) => <span key={tg} className="text-gold">#{tg}</span>)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
